import { fetchGames } from '@/api/library';
import { fetchRoadmapItems } from '@/api/roadmap';
import { displayTitle } from '@/lib/displayTitle';
import { gamePoster, gamePosterFallback, gameWideThumb } from '@/lib/gameArt';
import type { HubGame, RoadmapItem } from '@/types';

export interface UpcomingCard {
  id: string;
  title: string;
  date?: string;
  href: string;
  art: string;
  fallback: string;
  category: string;
  status?: string;
  description?: string;
  playable: boolean;
}

const DONE = new Set(['shipped', 'released', 'live', 'done', 'published']);

function isUpcoming(status?: string) {
  return !DONE.has((status ?? '').toLowerCase());
}

function byDate(a: UpcomingCard, b: UpcomingCard) {
  if (!a.date) return 1;
  if (!b.date) return -1;
  return a.date.localeCompare(b.date);
}

export function formatReleaseDate(value: string | undefined, locale: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function artForGame(game: HubGame) {
  return gameWideThumb(game) ?? gamePoster(game);
}

function fromRoadmap(item: RoadmapItem): UpcomingCard {
  const stub: HubGame = {
    id: item.gameId ?? item.id,
    title: item.title,
    category: item.category,
  };
  return {
    id: `roadmap-${item.id}`,
    title: displayTitle(item.title),
    date: item.targetDate,
    href: item.gameId ? `/library/${encodeURIComponent(item.gameId)}` : '/roadmap',
    art: artForGame(stub),
    fallback: gamePosterFallback(stub),
    category: item.category,
    status: item.status,
    description: item.description,
    playable: Boolean(item.gameId),
  };
}

function fromGame(game: HubGame): UpcomingCard {
  return {
    id: `game-${game.id}`,
    title: displayTitle(game.title),
    date: game.details?.releaseDate ?? undefined,
    href: `/library/${encodeURIComponent(game.id)}`,
    art: artForGame(game),
    fallback: gamePosterFallback(game),
    category: game.category,
    status: game.flags?.isComingSoon ? 'coming_soon' : undefined,
    description: game.description,
    playable: true,
  };
}

/**
 * Release pipeline for the home rail and the roadmap page: published roadmap
 * entries first, then the catalog's coming-soon titles when the roadmap is
 * still empty.
 */
export async function fetchUpcoming(
  accessToken: string,
  sessionId: string | null | undefined,
  categories: string[] = ['slot', 'live'],
): Promise<UpcomingCard[]> {
  const roadmapLists = await Promise.all(
    categories.map((category) => fetchRoadmapItems(accessToken, { category, limit: 40 }, sessionId)),
  );

  const roadmap = roadmapLists
    .flat()
    .filter((item) => isUpcoming(item.status))
    .map(fromRoadmap);

  if (roadmap.length > 0) return roadmap.sort(byDate);

  const catalog = (
    await Promise.all(
      categories.map((category) => fetchGames(accessToken, { category, limit: 40 }, sessionId)),
    )
  ).flat();

  const soon = catalog.filter((game) => game.flags?.isComingSoon);
  return (soon.length > 0 ? soon : catalog).map(fromGame).sort(byDate);
}
